import type { IssueQuoteInput } from '@repo/api-contracts';
import { useState } from 'react';
import { useInvalidateQuotes } from '@/entities/quote';
import { trpc } from '@/shared/api';
import { getErrorMessage, getTrpcErrorCode } from '@/shared/lib';

export interface UseIssueQuoteResult {
  /** 성공하면 true. 검증 실패·설정 미비·충돌이면 false. */
  readonly issue: (input: IssueQuoteInput) => Promise<boolean>;
  readonly isPending: boolean;
  /** 서버 거절 문구. */
  readonly error: string | null;
  /** 오류 코드별 다음 행동 안내(검증 실패 → 초안 수정·저장, 설정 미비 → 개발팀 배포 요청). */
  readonly guidance: string | null;
  /** 초안·상태가 바뀌어(CONFLICT) 최신 초안을 다시 불러와야 하는 상태. */
  readonly isOutdated: boolean;
  readonly reset: () => void;
}

const ERROR_FALLBACK = '발행하지 못했습니다.';
/**
 * BAD_REQUEST 는 ISSUE_VALIDATION_FAILED·SHIPPING_LINES_INCOMPLETE(배송비 줄 누락)와 PRODUCT_UNAVAILABLE 모두 — 서버 문구를 보고
 * 고를 수 있게 두 경우를 적는다.
 */
const BAD_REQUEST_GUIDANCE =
  '검증 실패라면 품목 줄(1개 이상)·배송지마다 배송비 줄·합계(0원 초과)·제작 착수 시점·납기·공급 방법·유효기한(만료일, 오늘 이후)을 확인해 임시 저장한 뒤 다시 발행하고, 상품·색상 문제라면 상품 설정(공개 여부·색상 공급 가능)을 확인해 주세요.';
/** 발행 설정은 코드 상수로 배포한다(견적 설계 §14) — 관리자 화면에서 등록하는 곳이 없다. */
const NOT_CONFIGURED_GUIDANCE =
  '발행 설정(공급자 정보·공통 문구)은 아직 배포되지 않았습니다. 개발팀에 발행 설정 배포를 요청해 주세요.';

const guidanceFor = (code: string | null): string | null => {
  if (code === 'BAD_REQUEST') return BAD_REQUEST_GUIDANCE;
  if (code === 'PRECONDITION_FAILED') return NOT_CONFIGURED_GUIDANCE;
  return null;
};

/**
 * 발행(quoteManagement.issue, 견적 설계 §6-4). 서버가 저장된 초안으로 발행하므로 편집 중인 값은 먼저 임시 저장해야 한다.
 * 입력의 `expectedDraftUpdatedAt` 은 화면이 본 초안의 토큰이다 — 다른 운영자가 그 뒤 저장했으면 서버가 CONFLICT 로 거절한다.
 * ISSUE_VALIDATION_FAILED(BAD_REQUEST)·ISSUANCE_NOT_CONFIGURED(PRECONDITION_FAILED)는 문구와 안내를,
 * CONFLICT 는 "최신 초안 다시 불러오기" 상태를 돌려준다. 성공하면 상세·목록을 다시 불러온다.
 */
export const useIssueQuote = (): UseIssueQuoteResult => {
  const [error, setError] = useState<string | null>(null);
  const [guidance, setGuidance] = useState<string | null>(null);
  const [isOutdated, setIsOutdated] = useState(false);
  const invalidate = useInvalidateQuotes();
  const mutation = trpc.quoteManagement.issue.useMutation();

  const reset = (): void => {
    setError(null);
    setGuidance(null);
    setIsOutdated(false);
  };

  const issue = async (input: IssueQuoteInput): Promise<boolean> => {
    reset();

    try {
      await mutation.mutateAsync(input);
      await Promise.all([invalidate.detail(input.quoteRequestId), invalidate.list()]);
      return true;
    } catch (cause) {
      const code = getTrpcErrorCode(cause);

      if (code === 'CONFLICT') {
        setIsOutdated(true);
      } else {
        setError(getErrorMessage(cause, ERROR_FALLBACK));
        setGuidance(guidanceFor(code));
      }

      return false;
    }
  };

  return { issue, isPending: mutation.isPending, error, guidance, isOutdated, reset };
};
